import notificationService from '@utils/notificationService';
import i18next from 'i18next';

import Mixpanel from '@analytics/mixpanel';
import apiTasks from '@api/apiTasks';
import { invalidateGoogleIntegration } from '@redux/actions/profileActions';

export const START_REQUEST = 'START_REQUEST';
export const STOP_REQUEST = 'STOP_REQUEST';
export const FETCH_TASKS_SUCCESS = 'FETCH_TASKS_SUCCESS';
export const FETCH_SCHEDULED_TASKS_SUCCESS = 'FETCH_SCHEDULED_TASKS_SUCCESS';
export const UPDATE_TASK_SUCCESS = 'UPDATE_TASK_SUCCESS';
export const FETCH_SCHEDULED_TASKS_REQUEST = 'FETCH_SCHEDULED_TASKS_REQUEST';
export const FETCH_SCHEDULED_TASKS_FAILURE = 'FETCH_SCHEDULED_TASKS_FAILURE';
export const FETCH_TASKS_REQUEST = 'FETCH_TASKS_REQUEST';
export const FETCH_TASKS_FAILURE = 'FETCH_TASKS_FAILURE';

let fetchTasksAbortController = null;
let fetchScheduledTasksAbortController = null;

export const startRequest = () => ({ type: START_REQUEST });
export const stopRequest = () => ({ type: STOP_REQUEST });

export const fetchTasks = (lastId = '', pageSize = 10, query = '') => {
  return async dispatch => {
    if (fetchTasksAbortController) {
      fetchTasksAbortController.abort();
    }

    fetchTasksAbortController = new AbortController();

    dispatch({ type: FETCH_TASKS_REQUEST });

    try {
      const response = await apiTasks.fetchTasks(
        lastId,
        pageSize,
        query,
        fetchTasksAbortController.signal
      );

      dispatch({
        type: FETCH_TASKS_SUCCESS,
        payload: response.data,
      });

      fetchTasksAbortController = null;
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }

      dispatch({
        type: FETCH_TASKS_FAILURE,
        payload: error.message,
      });

      fetchTasksAbortController = null;
    }
  };
};

export const fetchScheduledTasks = (lastId = '', pageSize = 10, query = '') => {
  return async dispatch => {
    if (fetchScheduledTasksAbortController) {
      fetchScheduledTasksAbortController.abort();
    }

    fetchScheduledTasksAbortController = new AbortController();

    dispatch({ type: FETCH_SCHEDULED_TASKS_REQUEST });

    try {
      const response = await apiTasks.fetchScheduledTasks(
        lastId,
        pageSize,
        query,
        fetchScheduledTasksAbortController.signal
      );

      dispatch({
        type: FETCH_SCHEDULED_TASKS_SUCCESS,
        payload: response.data,
      });

      fetchScheduledTasksAbortController = null;
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }

      dispatch({
        type: FETCH_SCHEDULED_TASKS_FAILURE,
        payload: error.message,
      });

      fetchScheduledTasksAbortController = null;
    }
  };
};

export const rerunTask = (taskId) => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.rerunTask(taskId);
    notificationService.success({ message: data });

    const tasks = getState().tasksReducer.tasks;
    const hasMoreTasks = getState().tasksReducer.hasMoreTasks;

    for (let i = 0; i < tasks.length; i++) {
      if (tasks[i].id === taskId) {
        tasks[i].status = 'RESTARTED';
        break;
      }
    }

    dispatch({ tasks: [...tasks], hasMoreTasks, type: FETCH_TASKS_SUCCESS });
  } catch (error) {
    notificationService.error({ message: error.message, duration: 8 });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Rerun task');
};

export const rerunTasks = (taskIds) => async (dispatch, getState) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.rerunTasks(taskIds);
    notificationService.success({ message: data });

    const tasks = getState().tasksReducer.tasks;
    const hasMoreTasks = getState().tasksReducer.hasMoreTasks;

    for (let i = 0; i < tasks.length; i++) {
      if (taskIds.includes(tasks[i].id)) tasks[i].status = 'RESTARTED';
    }

    dispatch({
      type: FETCH_TASKS_SUCCESS,
      payload: {
        tasks: [...tasks],
        has_more: hasMoreTasks
      }
    });
  } catch (error) {
    notificationService.error({ message: error.message, duration: 8 });
  } finally {
    dispatch(stopRequest());
  }
};

export const updateTask = (taskId, payload) => async (dispatch) => {
  try {
    await apiTasks.updateTask(taskId, payload);
    dispatch({
      type: UPDATE_TASK_SUCCESS,
      payload: { taskId, updates: payload },
    });
    notificationService.success({ message: i18next.t('message.updatedSuccessfully') });
  } catch (error) {
    notificationService.error({ message: error.message, duration: 8 });
  }
};

export const removeTasks = (taskIds) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.removeTasks(taskIds);
    console.log('✅ Response:', data);
    console.log('✅ Showing notification...');
    notificationService.success({ message: data, duration: 10 }); // 10 секунд!
    console.log('✅ Notification shown');
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const scheduleTask = (taskId, crontab, timezone = null) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.scheduleTask(taskId, crontab, timezone);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Schedule task');
};

export const removeScheduledTasks = (taskIds) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.removeScheduledTasks(taskIds);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }
};

export const pauseScheduledTask = (taskId) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.pauseScheduledTask(taskId);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Pause scheduled task');
};

export const resumeScheduledTask = (taskId) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.resumeScheduledTask(taskId);
    notificationService.success({ message: data });
  } catch (error) {
    notificationService.error({ message: error.message });
  } finally {
    dispatch(stopRequest());
  }

  Mixpanel.track('Resume scheduled task');
};

export const exportToSpreadsheet = (taskIds, accessToken, refreshToken) => async (dispatch) => {
  dispatch(startRequest());
  try {
    const { data } = await apiTasks.exportToSpreadsheet(taskIds, accessToken, refreshToken);
    const { spreadsheet_urls } = data;
    return spreadsheet_urls;
  } catch (error) {
    const errorCode = error?.response?.data?.errorCode;
    const message = error?.response?.data?.errorMessage || error?.message || 'Could not export to Google Sheets. Please try again.';

    if (errorCode === 'google_token_corrupt' || message.includes('No refresh token found')) {
      // BR-005 (rashida): clear local credentials without revoking the token again.
      dispatch(invalidateGoogleIntegration());
      const authError = new Error(message);
      authError.type = 'GOOGLE_TOKEN_CORRUPT';
      throw authError;
    }

    // The export helper reports the failure once, including errors after reconnecting.
    throw new Error(message);
  } finally {
    dispatch(stopRequest());
    Mixpanel.track('Export to spreadsheet');
  }
};

export const updateScheduledTask = (taskId, task) => async (dispatch) => {
  dispatch(startRequest());
  try {
    await apiTasks.updateScheduledTask(taskId, task);
    return true;
  } catch (error) {
    notificationService.error({ message: error.message });
    return false;
  } finally {
    dispatch(stopRequest());
    Mixpanel.track('Update scheduled task');
  }
};

export const updateTaskSchedule = (taskId, crontab, timezone = null) => async (dispatch) => {
  dispatch(startRequest());
  try {
    await apiTasks.updateTaskSchedule(taskId, { crontab, timezone });
    notificationService.success({ message: i18next.t('message.scheduleUpdatedSuccessfully') });
    dispatch(fetchScheduledTasks());
  } catch (error) {
    notificationService.error({ message: error.message });
    dispatch(stopRequest());
  } finally {
    Mixpanel.track('Update task schedule');
  }
};
